import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Globe, Phone, Instagram, Facebook, MapPin, Plus, X, Star, Eye, Smartphone, ExternalLink, Mail, Calendar, ShoppingBag, Truck, Camera, Shield } from 'lucide-react';
import { adminApi } from '../../services/api';
import { useBranding } from '../../context/BrandingContext';
import { PageTooltip } from '../components/TooltipSystem';

const SOCIAL_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: 'IG', color: '#E1306C' },
  { id: 'facebook', name: 'Facebook', icon: 'f', color: '#1877F2' },
  { id: 'tiktok', name: 'TikTok', icon: 'TT', color: '#000000' },
  { id: 'twitter', name: 'X / Twitter', icon: 'X', color: '#1DA1F2' },
  { id: 'youtube', name: 'YouTube', icon: 'YT', color: '#FF0000' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'in', color: '#0A66C2' },
];

const REVIEW_PLATFORMS = [
  { id: 'google', name: 'Google', icon: 'G', color: '#4285F4' },
  { id: 'tripadvisor', name: 'TripAdvisor', icon: 'TA', color: '#34E0A1' },
  { id: 'facebook', name: 'Facebook', icon: 'f', color: '#1877F2' },
  { id: 'custom', name: 'Altro', icon: '?', color: '#888' },
];

export default function AdminContacts() {
  const branding = useBranding();
  const [contacts, setContacts] = useState({
    website: '', phone: '', email: '',
    social: [],
    reservation_links: [],
    takeaway_links: [],
    delivery_links: [],
    shared_phone_enabled: false,
    shared_phone: '',
    order_notifications_email: '',
    // legacy mirrors (kept for other consumers)
    reservation_url: '', reservation_phone: '',
    takeaway_url: '', takeaway_phone: '',
  });
  const [reviewLinks, setReviewLinks] = useState([]);
  const [reviewBonusPoints, setReviewBonusPoints] = useState(0);
  const [spConfig, setSpConfig] = useState({
    enabled: false, instagram_handle: '', review_screenshot_bonus: 100,
    instagram_tag_bonus: 150, ai_confidence_threshold: 0.7,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      adminApi.get('/admin/contacts'),
      adminApi.get('/admin/review-links'),
      adminApi.get('/admin/social-proof-config'),
    ]).then(([cRes, rRes, spRes]) => {
      if (cRes.data && typeof cRes.data === 'object' && !Array.isArray(cRes.data)) {
        setContacts(c => ({ ...c, ...cRes.data }));
      }
      setReviewLinks(rRes.data?.links || rRes.data || []);
      setReviewBonusPoints(rRes.data?.bonus_points || 0);
      if (spRes.data) setSpConfig(c => ({ ...c, ...spRes.data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        adminApi.put('/admin/contacts', contacts),
        adminApi.put('/admin/review-links', { links: reviewLinks, bonus_points: reviewBonusPoints }),
        adminApi.put('/admin/social-proof-config', spConfig),
      ]);
      toast.success('Contatti e link salvati!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  const bg = branding.background_color || '#111114';
  const pc = branding.primary_color || '#D44E20';
  const tc = branding.text_color || '#FFFFFF';
  const ts = branding.text_secondary_color || '#888888';

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-6xl" data-testid="admin-contacts-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Contatti e Link</h1>
        <p className="text-[#64748B] text-sm">Sito web, social, telefono, prenotazioni e link recensioni</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* General */}
          <Section title="Informazioni generali" icon={<Globe size={16} />}>
            <Field label="Sito web" placeholder="https://www.tuosito.it" value={contacts.website}
              onChange={v => setContacts(c => ({ ...c, website: v }))} testid="contact-website" />
            <Field label="Telefono" placeholder="+39 02 1234567" value={contacts.phone}
              onChange={v => setContacts(c => ({ ...c, phone: v }))} testid="contact-phone" />
            <Field label="Email" placeholder="info@tuosito.it" value={contacts.email}
              onChange={v => setContacts(c => ({ ...c, email: v }))} testid="contact-email" />
          </Section>

          {/* Notifiche ordini admin */}
          <Section title="Notifiche ordini" icon={<Mail size={16} />}>
            <div>
              <label className="block text-xs font-semibold text-[#475569] mb-1">
                Email per notifiche ordini incassati
              </label>
              <input
                type="text"
                value={contacts.order_notifications_email || ''}
                onChange={e => setContacts(c => ({ ...c, order_notifications_email: e.target.value }))}
                placeholder="ordini@miobrand.it, gestore@miobrand.it"
                data-testid="contact-order-notifications-email"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none"
              />
              <p className="text-xs text-[#94A3B8] mt-1.5 leading-snug">
                Riceverai una email dettagliata per ogni ordine online incassato:
                biglietti evento (Stripe / PayPal), ordini shop e vendite cassa.
                Per più destinatari separa con la virgola. Lascia vuoto per
                disattivare.
              </p>
            </div>
          </Section>

          {/* Social */}
          <Section title="Profili social" icon={<Instagram size={16} />}>
            <div className="space-y-2">
              {contacts.social.map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`social-row-${i}`}>
                  <select value={s.platform} onChange={e => {
                    const updated = [...contacts.social];
                    const pl = SOCIAL_PLATFORMS.find(p => p.id === e.target.value);
                    updated[i] = { ...updated[i], platform: e.target.value, name: pl?.name || e.target.value };
                    setContacts(c => ({ ...c, social: updated }));
                  }} className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-2 text-sm focus:border-[#D44E20] focus:outline-none">
                    {SOCIAL_PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input value={s.url} onChange={e => {
                    const updated = [...contacts.social];
                    updated[i] = { ...updated[i], url: e.target.value };
                    setContacts(c => ({ ...c, social: updated }));
                  }} placeholder="https://..." className="flex-1 h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
                  <button onClick={() => setContacts(c => ({ ...c, social: c.social.filter((_, idx) => idx !== i) }))}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[#EF4444] hover:bg-red-50"><X size={14} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setContacts(c => ({ ...c, social: [...c.social, { platform: 'instagram', name: 'Instagram', url: '' }] }))}
              data-testid="add-social-btn" className="text-[#D44E20] text-xs font-medium flex items-center gap-1 mt-2 hover:underline">
              <Plus size={12} /> Aggiungi social
            </button>
          </Section>

          {/* Reservations / Take-away / Delivery */}
          <Section title="Prenotazioni, Take-away e Delivery" icon={<MapPin size={16} />}>
            <p className="text-[#64748B] text-xs mb-3">Aggiungi quanti link vuoi per ciascun servizio (es. TheFork per le prenotazioni, Uber Eats e Deliveroo per il delivery).</p>

            {/* Shared phone */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!contacts.shared_phone_enabled}
                  onChange={e => setContacts(c => ({ ...c, shared_phone_enabled: e.target.checked }))}
                  data-testid="shared-phone-toggle"
                  className="mt-0.5 w-4 h-4 flex-shrink-0"
                  style={{ accentColor: '#D44E20' }}
                />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-[#1A1A1A]">Usa lo stesso numero per tutti e 3 i servizi</p>
                  <p className="text-[10px] text-[#64748B] mt-0.5">Quando attivo, il numero indicato qui sotto vale per prenotazioni, take-away e delivery; il numero per singolo link viene ignorato.</p>
                </div>
              </label>
              {contacts.shared_phone_enabled && (
                <div className="mt-3 ml-6">
                  <Field
                    label="Numero condiviso"
                    placeholder="+39 02 1234567"
                    value={contacts.shared_phone}
                    onChange={v => setContacts(c => ({ ...c, shared_phone: v }))}
                    testid="shared-phone-input"
                  />
                </div>
              )}
            </div>

            <ServiceLinksList
              kind="reservation"
              title="Prenotazioni"
              icon={<Calendar size={14} />}
              addLabel="Aggiungi link prenotazione"
              placeholderLabel="es. TheFork, MIO numero"
              links={contacts.reservation_links}
              setLinks={updater => setContacts(c => ({ ...c, reservation_links: typeof updater === 'function' ? updater(c.reservation_links || []) : updater }))}
              sharedActive={!!contacts.shared_phone_enabled}
            />
            <ServiceLinksList
              kind="takeaway"
              title="Take-away"
              icon={<ShoppingBag size={14} />}
              addLabel="Aggiungi link take-away"
              placeholderLabel="es. Sito ordini, Glovo Pickup"
              links={contacts.takeaway_links}
              setLinks={updater => setContacts(c => ({ ...c, takeaway_links: typeof updater === 'function' ? updater(c.takeaway_links || []) : updater }))}
              sharedActive={!!contacts.shared_phone_enabled}
            />
            <ServiceLinksList
              kind="delivery"
              title="Delivery"
              icon={<Truck size={14} />}
              addLabel="Aggiungi link delivery"
              placeholderLabel="es. Uber Eats, Deliveroo, Glovo"
              links={contacts.delivery_links}
              setLinks={updater => setContacts(c => ({ ...c, delivery_links: typeof updater === 'function' ? updater(c.delivery_links || []) : updater }))}
              sharedActive={!!contacts.shared_phone_enabled}
            />
          </Section>

          {/* Review Links */}
          <Section title="Link Recensioni" icon={<Star size={16} />}>
            <p className="text-[#64748B] text-xs mb-3">Piattaforme dove i clienti possono lasciare recensioni</p>
            <div className="space-y-2">
              {reviewLinks.map((link, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`review-row-${i}`}>
                  <select value={link.platform} onChange={e => {
                    const updated = [...reviewLinks];
                    const pl = REVIEW_PLATFORMS.find(p => p.id === e.target.value);
                    updated[i] = { ...updated[i], platform: e.target.value, name: pl?.name || e.target.value };
                    setReviewLinks(updated);
                  }} className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-2 text-sm focus:border-[#D44E20] focus:outline-none">
                    {REVIEW_PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input value={link.url} onChange={e => {
                    const updated = [...reviewLinks];
                    updated[i] = { ...updated[i], url: e.target.value };
                    setReviewLinks(updated);
                  }} placeholder="https://..." className="flex-1 h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
                  <button onClick={() => setReviewLinks(reviewLinks.filter((_, idx) => idx !== i))}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[#EF4444] hover:bg-red-50"><X size={14} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setReviewLinks([...reviewLinks, { platform: 'google', name: 'Google', url: '' }])}
              data-testid="add-review-btn" className="text-[#D44E20] text-xs font-medium flex items-center gap-1 mt-2 hover:underline">
              <Plus size={12} /> Aggiungi piattaforma
            </button>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Punti bonus per click recensione</label>
              <p className="text-[10px] text-[#64748B] mb-2">Il cliente riceve questi punti la prima volta che clicca su un link recensione (una sola volta, indipendentemente dalla piattaforma)</p>
              <input type="number" min="0" value={reviewBonusPoints} onChange={e => setReviewBonusPoints(parseInt(e.target.value) || 0)}
                data-testid="review-bonus-points-input"
                className="w-32 h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
          </Section>

          {/* Social Proof Config */}
          <Section title="Prova Sociale (AI)" icon={<Camera size={16} />}>
            <p className="text-[#64748B] text-xs mb-3">I clienti caricano screenshot di recensioni o tag Instagram. L'AI verifica automaticamente e assegna punti bonus.</p>
            <div className="flex items-center justify-between mb-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <p className="text-sm font-medium text-[#1A1A1A]">Attiva Prova Sociale</p>
                <p className="text-[10px] text-[#64748B]">Mostra la sezione "Guadagna punti extra" nel portale cliente</p>
              </div>
              <button
                onClick={() => setSpConfig(c => ({ ...c, enabled: !c.enabled }))}
                data-testid="social-proof-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${spConfig.enabled ? 'bg-[#10B981]' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${spConfig.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {spConfig.enabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Handle Instagram</label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-[#64748B]">@</span>
                    <input
                      value={spConfig.instagram_handle}
                      onChange={e => setSpConfig(c => ({ ...c, instagram_handle: e.target.value }))}
                      data-testid="sp-instagram-handle"
                      placeholder="tacoloco.food"
                      className="flex-1 h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Punti screenshot recensione</label>
                    <input
                      type="number" min="0"
                      value={spConfig.review_screenshot_bonus}
                      onChange={e => setSpConfig(c => ({ ...c, review_screenshot_bonus: parseInt(e.target.value) || 0 }))}
                      data-testid="sp-review-bonus"
                      className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Punti tag Instagram</label>
                    <input
                      type="number" min="0"
                      value={spConfig.instagram_tag_bonus}
                      onChange={e => setSpConfig(c => ({ ...c, instagram_tag_bonus: parseInt(e.target.value) || 0 }))}
                      data-testid="sp-instagram-bonus"
                      className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Soglia fiducia AI (0.0 - 1.0)</label>
                  <p className="text-[10px] text-[#64748B] mb-2">Se l'AI ha un livello di confidenza sotto questa soglia, la prova va in coda per revisione manuale</p>
                  <input
                    type="number" min="0" max="1" step="0.1"
                    value={spConfig.ai_confidence_threshold}
                    onChange={e => setSpConfig(c => ({ ...c, ai_confidence_threshold: parseFloat(e.target.value) || 0.7 }))}
                    data-testid="sp-threshold"
                    className="w-32 h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none"
                  />
                </div>
              </div>
            )}
          </Section>

          {/* Save */}
          <button onClick={handleSave} disabled={saving} data-testid="save-contacts-btn"
            className="w-full h-11 bg-[#D44E20] text-white text-sm font-bold rounded-xl hover:bg-[#B83D15] disabled:opacity-50 transition-colors">
            {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </button>
        </div>

        {/* Right: Live Preview */}
        <div className="lg:col-span-3" data-testid="contacts-preview-section">
          <div className="sticky top-8">
            <div className="flex items-center gap-2 mb-3">
              <Eye size={16} className="text-[#64748B]" />
              <span className="text-sm font-medium text-[#64748B]">Anteprima bacheca cliente</span>
            </div>

            {/* Phone frame */}
            <div className="mx-auto w-[320px] rounded-[32px] border-4 border-gray-800 bg-gray-800 p-2 shadow-2xl">
              <div className="rounded-[24px] overflow-hidden overflow-y-auto" style={{ backgroundColor: bg, minHeight: 560, maxHeight: 560 }}>
                <ContactsPreview
                  contacts={contacts}
                  reviewLinks={reviewLinks}
                  bg={bg} pc={pc} tc={tc} ts={ts}
                  businessName={branding.business_name || 'Il tuo locale'}
                  logoUrl={branding.logo_url}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Phone Preview ─────── */
function ContactsPreview({ contacts, reviewLinks, bg, pc, tc, ts, businessName, logoUrl }) {
  const hasSocial = contacts.social.length > 0;
  const hasReviews = reviewLinks.length > 0;
  const reservationLinks = contacts.reservation_links || [];
  const takeawayLinks = contacts.takeaway_links || [];
  const deliveryLinks = contacts.delivery_links || [];
  const hasReservation = reservationLinks.length > 0;
  const hasTakeaway = takeawayLinks.length > 0;
  const hasDelivery = deliveryLinks.length > 0;
  const hasContact = contacts.website || contacts.phone || contacts.email;
  const isEmpty = !hasSocial && !hasReviews && !hasReservation && !hasTakeaway && !hasDelivery && !hasContact;

  return (
    <div className="p-5 pt-8">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        {logoUrl && <img src={logoUrl} alt="" className="w-8 h-8 rounded-lg object-contain" />}
        <p className="text-xs font-medium" style={{ color: ts }}>{businessName}</p>
      </div>
      <p className="text-lg font-bold mb-5" style={{ color: tc }}>Ciao Marco!</p>

      {/* Points card (decorative context) */}
      <div className="rounded-2xl p-3 border border-white/10 bg-white/5 mb-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px]" style={{ color: ts }}>I tuoi punti</p>
            <p className="text-xl font-bold" style={{ color: tc }}>340</p>
          </div>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${pc}20` }}>
            <Star size={20} style={{ color: pc }} />
          </div>
        </div>
      </div>

      {isEmpty && (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-3">
            <Globe size={20} style={{ color: ts }} />
          </div>
          <p className="text-xs" style={{ color: ts }}>Aggiungi contatti, social o link<br />recensioni per vederli qui</p>
        </div>
      )}

      {/* Review Links */}
      {hasReviews && (
        <div className="mb-5">
          <p className="text-xs font-bold mb-2" style={{ color: tc }}>Lascia una recensione</p>
          <div className="space-y-1.5">
            {reviewLinks.map((r, i) => {
              const p = REVIEW_PLATFORMS.find(rp => rp.id === r.platform) || { icon: '?', color: '#888', name: 'Altro' };
              return (
                <div key={i} className="flex items-center gap-2.5 rounded-xl p-3 border border-white/5 bg-white/5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0" style={{ backgroundColor: p.color }}>
                    {p.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: tc }}>{r.name || p.name}</p>
                    <p className="text-[9px] truncate" style={{ color: ts }}>Lascia una recensione su {r.name || p.name}</p>
                  </div>
                  <ExternalLink size={12} style={{ color: ts }} className="flex-shrink-0" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reservation / Takeaway / Delivery */}
      {(hasReservation || hasTakeaway || hasDelivery) && (
        <div className="mb-5">
          <p className="text-xs font-bold mb-2" style={{ color: tc }}>Prenota o ordina</p>
          <div className="grid grid-cols-3 gap-1.5">
            {hasReservation && (
              <div className="rounded-xl p-3 border text-center" style={{ borderColor: `${pc}30`, backgroundColor: `${pc}10` }}>
                <Calendar size={16} className="mx-auto mb-1" style={{ color: pc }} />
                <p className="text-[10px] font-semibold" style={{ color: pc }}>Prenota</p>
                <p className="text-[9px] mt-0.5" style={{ color: ts }}>{reservationLinks.length} link</p>
              </div>
            )}
            {hasTakeaway && (
              <div className="rounded-xl p-3 border text-center" style={{ borderColor: `${pc}30`, backgroundColor: `${pc}10` }}>
                <ShoppingBag size={16} className="mx-auto mb-1" style={{ color: pc }} />
                <p className="text-[10px] font-semibold" style={{ color: pc }}>Take-away</p>
                <p className="text-[9px] mt-0.5" style={{ color: ts }}>{takeawayLinks.length} link</p>
              </div>
            )}
            {hasDelivery && (
              <div className="rounded-xl p-3 border text-center" style={{ borderColor: `${pc}30`, backgroundColor: `${pc}10` }}>
                <Truck size={16} className="mx-auto mb-1" style={{ color: pc }} />
                <p className="text-[10px] font-semibold" style={{ color: pc }}>Delivery</p>
                <p className="text-[9px] mt-0.5" style={{ color: ts }}>{deliveryLinks.length} link</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Social Links */}
      {hasSocial && (
        <div className="mb-5">
          <p className="text-xs font-bold mb-2" style={{ color: tc }}>Seguici</p>
          <div className="flex flex-wrap gap-2">
            {contacts.social.map((s, i) => {
              const p = SOCIAL_PLATFORMS.find(sp => sp.id === s.platform) || { icon: '?', color: '#888' };
              return (
                <div key={i} className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: p.color }}>
                  {p.icon}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contact Info Footer */}
      {hasContact && (
        <div className="border-t border-white/5 pt-4 mt-4 space-y-2">
          {contacts.website && (
            <div className="flex items-center gap-2">
              <Globe size={12} style={{ color: ts }} />
              <p className="text-[10px] truncate" style={{ color: ts }}>{contacts.website}</p>
            </div>
          )}
          {contacts.phone && (
            <div className="flex items-center gap-2">
              <Phone size={12} style={{ color: ts }} />
              <p className="text-[10px]" style={{ color: ts }}>{contacts.phone}</p>
            </div>
          )}
          {contacts.email && (
            <div className="flex items-center gap-2">
              <Mail size={12} style={{ color: ts }} />
              <p className="text-[10px] truncate" style={{ color: ts }}>{contacts.email}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
      <h3 className="font-semibold text-[#1A1A1A] text-sm mb-4 flex items-center gap-2">{icon} {title}</h3>
      {children}
    </div>
  );
}

function Field({ label, placeholder, value, onChange, testid }) {
  return (
    <div className="mb-3 last:mb-0">
      <PageTooltip page="contacts" />
      <label className="text-[10px] text-[#64748B] font-medium block mb-1">{label}</label>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
    </div>
  );
}

function ServiceLinksList({ kind, title, icon, addLabel, placeholderLabel, links, setLinks, sharedActive }) {
  const list = Array.isArray(links) ? links : [];
  const update = (i, patch) => setLinks(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const remove = (i) => setLinks(prev => prev.filter((_, idx) => idx !== i));
  const add = () => setLinks(prev => [...prev, { label: '', url: '', phone: '' }]);

  return (
    <div className="mb-5 last:mb-0" data-testid={`service-links-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[#1A1A1A] flex items-center gap-1.5">
          <span className="text-[#D44E20]">{icon}</span> {title}
        </p>
        <span className="text-[10px] text-[#64748B]">{list.length} link</span>
      </div>
      <div className="space-y-2">
        {list.map((l, i) => (
          <div key={i} data-testid={`service-link-${kind}-${i}`}
            className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                value={l.label || ''}
                onChange={e => update(i, { label: e.target.value })}
                placeholder={placeholderLabel}
                data-testid={`service-link-${kind}-${i}-label`}
                className="flex-1 h-9 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm focus:border-[#D44E20] focus:outline-none"
              />
              <button type="button" onClick={() => remove(i)}
                data-testid={`service-link-${kind}-${i}-remove`}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-[#EF4444] hover:bg-red-50">
                <X size={14} />
              </button>
            </div>
            <input
              value={l.url || ''}
              onChange={e => update(i, { url: e.target.value })}
              placeholder="https://..."
              data-testid={`service-link-${kind}-${i}-url`}
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm focus:border-[#D44E20] focus:outline-none"
            />
            <input
              value={sharedActive ? '' : (l.phone || '')}
              onChange={e => update(i, { phone: e.target.value })}
              placeholder={sharedActive ? 'Numero condiviso (impostato sopra)' : '+39 ... (opzionale)'}
              disabled={sharedActive}
              data-testid={`service-link-${kind}-${i}-phone`}
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm focus:border-[#D44E20] focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>
        ))}
      </div>
      <button type="button" onClick={add} data-testid={`service-link-${kind}-add`}
        className="text-[#D44E20] text-xs font-medium flex items-center gap-1 mt-2 hover:underline">
        <Plus size={12} /> {addLabel}
      </button>
    </div>
  );
}
